'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { log } from '@/services/appMonitoringSuite';

import FullscreenSpinner from './FullscreenSpinner';

/**
 * Single owner for every loading spinner in the app.
 *
 * Components never render a spinner themselves. They register a request with
 * `useSpinner(active, label)` and the provider renders exactly one
 * `<FullscreenSpinner />` for as long as at least one request is open. Two
 * components asking at once is normal and expected — it still produces one
 * spinner, because there is only one render site.
 *
 * One render site is the whole point: the app-level overlay and a page's own
 * `<Spinner />` used to be separate, coordinated by hand through
 * `isNavigating()` from `usePageState`, and that coordination silently failed
 * on back navigation and painted two spinner marks at once. (`isNavigating()`
 * itself is still around for unrelated focus handling - it is not a leftover.)
 */

/**
 * How much of the screen a request is entitled to.
 */
export enum SpinnerVariant {
  /** Dims everything behind the spinner and swallows input. For a load that
   * makes the whole screen unusable — startup, navigation, a save in flight. */
  BLOCKING = 'blocking',
  /** Same centred, full-viewport spinner, minus the scrim: the page stays
   * visible, undimmed and clickable behind it. For a page whose other content
   * is already rendered and readable while one region of it loads.
   *
   * Note this is a scrim switch, not a placement one — the mark still lands at
   * the centre of the viewport, not inside the region that is loading. */
  QUIET = 'quiet',
}

type SpinnerRequest = {
  label: string;
  variant: SpinnerVariant;
  /** Overdue past `STUCK_AFTER_MS`, and so no longer entitled to a scrim. */
  stuck?: boolean;
};

type SpinnerRegistry = Record<string, SpinnerRequest>;

type SpinnerContextValue = {
  open: (key: string, label: string, variant: SpinnerVariant) => void;
  close: (key: string) => void;
};

/**
 * Carries `open`/`close` and nothing else. Both are stable for the life of the
 * provider, so the value never changes identity and registering a spinner does
 * not re-render every other component that has one.
 */
const SpinnerContext = createContext<SpinnerContextValue | undefined>(undefined);

/**
 * How long a single request may hold the spinner before we treat it as stuck.
 *
 * Passing this logs the request and strips its scrim — see `stuck` on
 * `SpinnerRequest`. The spinner mark itself stays up: we know the load is
 * overdue, not that it failed, and a load that is merely slow should still look
 * like one. What the user gets back is the screen behind it and their remote.
 */
const STUCK_AFTER_MS = 30_000;

export const SpinnerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [requests, setRequests] = useState<SpinnerRegistry>({});

  /**
   * Each request is timed from its own open, not from the last change to the
   * registry — one spinner opening does not reprieve another that is already
   * hanging. The one thing that does restart a clock is the owning
   * `useSpinner` re-registering, which a changing `label` will do; see there.
   */
  const watchdogs = useRef<
    Record<string, { timer: ReturnType<typeof setTimeout>; openedAt: number }>
  >({});

  const open = useCallback((key: string, label: string, variant: SpinnerVariant) => {
    setRequests((prev) =>
      prev[key]?.label === label && prev[key]?.variant === variant
        ? prev
        : { ...prev, [key]: { label, variant } }
    );

    if (!watchdogs.current[key]) {
      watchdogs.current[key] = {
        openedAt: Date.now(),
        timer: setTimeout(() => {
          log(
            `[spinner] '${label}' still up after ${STUCK_AFTER_MS / 1000}s - releasing its scrim`,
            'error'
          );
          // A blocking request that never closes is the worst failure this
          // provider can produce: a fixed z-50 overlay that also eats every input
          setRequests((prev) =>
            prev[key] ? { ...prev, [key]: { ...prev[key], stuck: true } } : prev
          );
        }, STUCK_AFTER_MS),
      };
    }
  }, []);

  const close = useCallback((key: string) => {
    setRequests((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

    if (!watchdogs.current[key]) return;
    const watchdog = watchdogs.current[key];
    clearTimeout(watchdog.timer);
    delete watchdogs.current[key];

    // Only worth reporting once it has already been logged as stuck.
    const heldFor = Date.now() - watchdog.openedAt;
    if (heldFor >= STUCK_AFTER_MS) {
      log(`[spinner] recovered after ${Math.round(heldFor / 1000)}s`, 'warn');
    }
  }, []);

  // Nothing is stuck if the tree is gone.
  useEffect(
    () => () => {
      Object.values(watchdogs.current).forEach(({ timer }) => clearTimeout(timer));
      watchdogs.current = {};
    },
    []
  );

  const active = useMemo(() => Object.values(requests), [requests]);
  const activeLabels = useMemo(() => active.map((request) => request.label), [active]);
  // The most demanding request wins: one blocking load dims the screen even if
  // other requests are non-blocking. Stuck requests are excluded so a watchdog
  // timeout releases the scrim rather than leaving the screen dimmed.
  const scrim = active.some(
    (request) => request.variant === SpinnerVariant.BLOCKING && !request.stuck
  );

  // Contention is no longer a rendering bug, but it still points at redundant
  // loading states — two things fetching for the same transition.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development' || activeLabels.length < 2) return;
    // eslint-disable-next-line no-console
    console.warn(`[spinner] ${activeLabels.length} concurrent requests:`, activeLabels);
  }, [activeLabels]);

  const value = useMemo<SpinnerContextValue>(() => ({ open, close }), [open, close]);

  return (
    <SpinnerContext.Provider value={value}>
      {children}
      {active.length > 0 && <FullscreenSpinner scrim={scrim} />}
    </SpinnerContext.Provider>
  );
};

/**
 * Ask the root to show the spinner while `active` is true.
 *
 * `label` is only ever read for debugging; identity comes from a per-instance
 * key, so two mounts of the same component do not clobber each other. Keep it
 * a constant anyway — it is an effect dependency, so interpolating state into
 * it re-registers the request on every change and restarts its stuck-detection
 * clock, which quietly puts the watchdog out of reach.
 *
 * `variant` says how much of the screen the load owns — see `SpinnerVariant`.
 * It defaults to `BLOCKING`, which is the safe reading of an unqualified
 * "the app is busy".
 *
 * Safe to call outside a provider — it simply does nothing — so that components
 * can be unit-tested in isolation without wiring up the whole app tree. That
 * silence is why `useAssertSpinnerProvider` exists.
 */
export const useSpinner = (
  active: boolean,
  label: string,
  variant: SpinnerVariant = SpinnerVariant.BLOCKING
): void => {
  const context = useContext(SpinnerContext);
  const key = useId();

  const open = context?.open;
  const close = context?.close;

  useEffect(() => {
    if (!active || !open || !close) return;
    open(key, label, variant);
    return () => close(key);
  }, [active, key, label, variant, open, close]);
};

/**
 * Assert that a provider is mounted above this point in the tree.
 *
 * `useSpinner` deliberately no-ops without a provider, which keeps components
 * unit-testable in isolation but means a tree that loses the provider — moved
 * below `App`, or a route group given a layout without it — would drop every
 * loading state in the app with nothing to show for it. Converted pages render
 * `null` while loading, so that failure now looks like a dead black screen on
 * every load app-wide, not merely a missing spinner.
 *
 * Called once from `App`, which the root layout mounts under the provider
 * (behind a `<Suspense>` whose own fallback is a plain black screen rather than
 * a registry request). It runs during render rather than in an effect so the
 * throw is attributed to `App` itself and lands before anything commits, and so
 * it cannot depend on `useSpinner`'s effect ever running.
 */
export const useAssertSpinnerProvider = (): void => {
  const context = useContext(SpinnerContext);
  const reported = useRef(false);

  if (context) return;

  const message =
    'SpinnerProvider is missing above App - every loading state in the app is a no-op';
  if (process.env.NODE_ENV !== 'production') throw new Error(message);

  // Once per mount: this runs render-phase, and App re-renders on every route,
  // auth and navigation change for the life of the session.
  if (reported.current) return;
  reported.current = true;
  log(message, 'error');
};

/**
 * Declarative form, for the places a hook cannot reach.
 *
 * Usually that is a component with early returns above the point the spinner is
 * wanted, where a hook would run on some renders and not others. The other case
 * is a `<Suspense>` fallback, which only exists while the boundary is suspended
 * and so has nowhere to hang a hook at all.
 *
 * `variant` is optional and falls through to `useSpinner`'s `BLOCKING` default.
 */
export const SpinnerRequest: React.FC<{ label: string; variant?: SpinnerVariant }> = ({
  label,
  variant,
}) => {
  useSpinner(true, label, variant);
  return null;
};
